import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useSEO, generateOrganizationSchema, generateBreadcrumbSchema } from '../../utils/seo';

export default function AboutPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useSEO({
    title: '会社概要 | おかやま屋根工事株式会社 | 岡山市の屋根工事専門店',
    description: '岡山市で10年以上、2,000棟以上の施工実績を持つおかやま屋根工事株式会社の会社概要。代表挨拶、会社情報、沿革、アクセスをご紹介。地域密着・自社職人施工で確かな技術と適正価格を実現。',
    keywords: '岡山市,屋根工事,会社概要,おかやま屋根工事,岡山市北区,地域密着,自社職人',
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        generateOrganizationSchema(),
        generateBreadcrumbSchema([
          { name: 'ホーム', url: '/' },
          { name: '会社概要', url: '/about' }
        ])
      ]
    }
  });

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative h-[60vh] min-h-[500px] flex items-center justify-center">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/437ccc212ddbb006d436698d0849b5a3.jpg"
            alt="おかやま屋根工事株式会社"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/50"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">会社概要</h1>
          <p className="text-lg sm:text-xl text-white/90 max-w-2xl mx-auto">
            地域に根ざして20年以上、確かな技術で岡山の屋根を守り続けています
          </p>
        </div>
      </section>

      {/* CEO Message */}
      <section className="py-16 sm:py-20 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center">
            <div className="order-2 lg:order-1">
              <span className="inline-block px-4 py-2 bg-[#1e3a5f] text-white text-sm font-semibold rounded-full mb-6">
                代表挨拶
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
                お客様の大切な住まいを<br />
                守り続けることが<br />
                私たちの使命です
              </h2>
              <div className="space-y-4 text-gray-600 leading-relaxed text-sm sm:text-base">
                <p>
                  おかやま屋根工事株式会社のホームページをご覧いただき、誠にありがとうございます。代表取締役の岡山太郎と申します。
                </p>
                <p>
                  当社は2000年の設立以来、岡山市を中心に2,000棟以上の屋根工事を手がけてまいりました。屋根は住まいを雨風から守る最も重要な部分です。だからこそ、私たちは一切の妥協を許さず、確かな技術と誠実な対応でお客様の信頼にお応えしてまいりました。
                </p>
                <p>
                  自社職人による直接施工にこだわり、下請けに丸投げすることは一切ありません。経験豊富な職人が責任を持って施工することで、高品質な工事を適正価格でご提供できると考えております。
                </p>
                <p>
                  これからも地域密着の企業として、お客様の大切な住まいを守り続けることをお約束いたします。屋根のことでお困りのことがございましたら、どうぞお気軽にご相談ください。
                </p>
              </div>
              <div className="mt-8 pt-8 border-t border-gray-200">
                <p className="text-xl sm:text-2xl font-bold text-gray-900">代表取締役　岡山 太郎</p>
              </div>
            </div>
            
            <div className="order-1 lg:order-2">
              <div className="w-full h-[500px] sm:h-[600px]">
                <img
                  src="https://readdy.ai/api/search-image?query=Professional%20portrait%20of%20confident%20Japanese%20male%20CEO%20in%20business%20suit%20standing%20in%20modern%20office%2C%20mature%20businessman%20around%2050%20years%20old%20without%20any%20text%20or%20signage%2C%20showing%20leadership%20and%20trust&width=800&height=1000&seq=ceo001&orientation=portrait"
                  alt="代表取締役 岡山太郎"
                  className="w-full h-full object-cover object-top rounded-2xl shadow-2xl"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Company Info */}
      <section className="py-16 sm:py-20 lg:py-24 bg-gray-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900">会社情報</h2>
          </div>
          
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            <div className="divide-y divide-gray-200">
              <InfoRow label="会社名" value="おかやま屋根工事株式会社" />
              <InfoRow label="代表取締役" value="岡山 太郎" />
              <InfoRow label="所在地" value="〒700-0000 岡山市北区○○町1-2-3" />
              <InfoRow label="電話番号" value="086-012-3456" />
              <InfoRow label="FAX" value="086-012-3457" />
              <InfoRow label="設立" value="2000年" />
              <InfoRow label="従業員数" value="5名" />
              <InfoRow label="営業時間" value="9:00〜18:00" />
              <InfoRow label="事業内容" value="屋根工事全般（葺き替え、塗装、雨漏り修理、防水工事、雨樋工事など）" />
              <InfoRow label="対応エリア" value="岡山市を中心とした岡山県内" />
              <InfoRow label="許可・資格" value="建設業許可、一級建築士事務所登録" />
            </div>
          </div>
        </div>
      </section>

      {/* Access Map */}
      <section className="py-16 sm:py-20 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">アクセス</h2>
            <p className="text-base sm:text-lg text-gray-600">お気軽にお越しください</p>
          </div>
          
          <div className="bg-gray-100 rounded-2xl overflow-hidden shadow-lg mb-8">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d52634.64498084778!2d133.88235!3d34.66167!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x355402e59fffffff%3A0x4c1b4c8c8c8c8c8c!2z5bKh5bGx5biC5YyX5Yy6!5e0!3m2!1sja!2sjp!4v1234567890"
              width="100%"
              height="450"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="おかやま屋根工事株式会社 地図"
            ></iframe>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8">
            <div className="bg-gray-50 rounded-xl p-6 sm:p-8">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#1e3a5f] rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-map-pin-line text-xl text-white"></i>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">所在地</h3>
                  <p className="text-gray-600 text-sm sm:text-base">
                    〒700-0000<br />
                    岡山市北区○○町1-2-3
                  </p>
                </div>
              </div>
            </div>
            
            <div className="bg-gray-50 rounded-xl p-6 sm:p-8">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#1e3a5f] rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-train-line text-xl text-white"></i>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">交通案内</h3>
                  <p className="text-gray-600 text-sm sm:text-base">
                    JR岡山駅より車で約15分<br />
                    駐車場完備
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* History */}
      <section className="py-16 sm:py-20 lg:py-24 bg-gray-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900">沿革</h2>
          </div>
          
          <div className="space-y-8">
            <HistoryItem
              year="2000年"
              event="おかやま屋根工事株式会社設立。岡山市北区に本社を構え、屋根工事事業を開始。"
            />
            <HistoryItem
              year="2016年"
              event="施工実績100棟達成。地域のお客様から高い評価をいただく。"
            />
            <HistoryItem
              year="2018年"
              event="従業員5名体制に拡大。対応エリアを岡山県内全域に拡大。"
            />
            <HistoryItem
              year="2020年"
              event="施工実績1,000棟達成。10年保証制度を導入。"
            />
            <HistoryItem
              year="2022年"
              event="ドローンを活用した屋根診断サービスを開始。"
            />
            <HistoryItem
              year="2024年"
              event="施工実績2,000棟達成。創業10周年を迎える。"
            />
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-16 sm:py-20 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">私たちのチーム</h2>
            <p className="text-base sm:text-lg text-gray-600">経験豊富な職人がお客様の屋根を守ります</p>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            <TeamMember
              name="山田 次郎"
              position="工事部長"
              experience="経験20年"
              image="https://readdy.ai/api/search-image?query=Professional%20portrait%20of%20experienced%20Japanese%20male%20roofing%20craftsman%20in%20work%20uniform%2C%20confident%20middle-aged%20worker%20without%20any%20text%20or%20signage%2C%20showing%20expertise%20and%20reliability&width=400&height=500&seq=team001&orientation=portrait"
            />
            <TeamMember
              name="佐藤 花子"
              position="営業担当"
              experience="経験8年"
              image="https://readdy.ai/api/search-image?query=Professional%20portrait%20of%20friendly%20Japanese%20female%20sales%20representative%20in%20business%20casual%20attire%2C%20warm%20and%20approachable%20without%20any%20text%20or%20signage%2C%20showing%20professionalism&width=400&height=500&seq=team002&orientation=portrait"
            />
            <TeamMember
              name="田中 健一"
              position="職人"
              experience="経験15年"
              image="https://readdy.ai/api/search-image?query=Professional%20portrait%20of%20skilled%20Japanese%20male%20roofing%20worker%20in%20safety%20gear%2C%20experienced%20craftsman%20without%20any%20text%20or%20signage%2C%20showing%20dedication%20and%20skill&width=400&height=500&seq=team003&orientation=portrait"
            />
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-20 sm:py-24 lg:py-32">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Beautiful%20Okayama%20city%20skyline%20with%20clear%20blue%20sky%20and%20traditional%20Japanese%20architecture%2C%20peaceful%20urban%20landscape%20without%20any%20text%20or%20signage%2C%20professional%20cityscape%20photography&width=1920&height=800&seq=aboutcta001&orientation=landscape"
            alt="岡山市 街並み"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-black/50"></div>
        </div>
        
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6 sm:mb-8">
            無料見積もり・ご相談<br />
            今すぐお電話ください
          </h2>
          <p className="text-lg sm:text-xl text-white mb-8 sm:mb-12">
            営業時間 9:00〜18:00
          </p>
          
          <a
            href="tel:0860123456"
            className="inline-flex items-center justify-center gap-4 px-8 sm:px-12 py-5 sm:py-6 bg-[#ff6b35] rounded-2xl hover:bg-[#e55a28] transition-all duration-300 shadow-2xl cursor-pointer mb-6"
          >
            <div className="w-12 h-12 sm:w-14 sm:h-14 bg-white/20 rounded-full flex items-center justify-center">
              <i className="ri-phone-fill text-2xl sm:text-3xl text-white"></i>
            </div>
            <span className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white whitespace-nowrap">086-012-3456</span>
            <i className="ri-arrow-right-up-line text-2xl sm:text-3xl text-white"></i>
          </a>
          
          <p className="text-sm sm:text-base text-white/80">または</p>
          
          <Link
            to="/contact"
            className="inline-flex items-center justify-center px-8 py-4 mt-4 text-base sm:text-lg font-bold text-[#1e3a5f] bg-white rounded-lg hover:bg-gray-100 transition-all duration-300 shadow-lg whitespace-nowrap cursor-pointer"
          >
            お問い合わせフォームへ
            <i className="ri-mail-line ml-2 text-xl"></i>
          </Link>
        </div>
      </section>
    </div>
  );
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 px-6 sm:px-8 py-5 sm:py-6">
      <div className="text-gray-500 font-semibold text-sm sm:text-base">{label}</div>
      <div className="sm:col-span-2 text-gray-900 font-medium text-sm sm:text-base">{value}</div>
    </div>
  );
}

function HistoryItem({ year, event }: { year: string; event: string }) {
  return (
    <div className="flex gap-6 items-start">
      <div className="flex-shrink-0">
        <div className="w-24 h-24 bg-[#1e3a5f] rounded-full flex items-center justify-center">
          <span className="text-lg font-bold text-white text-center leading-tight">{year}</span>
        </div>
      </div>
      <div className="flex-1 pt-4">
        <p className="text-gray-700 leading-relaxed text-sm sm:text-base">{event}</p>
      </div>
    </div>
  );
}

function TeamMember({ name, position, experience, image }: { name: string; position: string; experience: string; image: string }) {
  return (
    <div className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300">
      <div className="h-80">
        <img
          src={image}
          alt={name}
          className="w-full h-full object-cover object-top"
        />
      </div>
      <div className="p-6">
        <h3 className="text-xl font-bold text-gray-900 mb-2">{name}</h3>
        <p className="text-[#1e3a5f] font-semibold mb-1 text-sm sm:text-base">{position}</p>
        <p className="text-gray-600 text-sm">{experience}</p>
      </div>
    </div>
  );
}
